import React from 'react';
import { Trash2, Plus, MapPin } from 'lucide-react';
import CompactConditionSelect from './CompactConditionSelect';

export const DynamicAreaDemandTable = ({ rows = [], onChange, patientConditions = [] }) => {
  const conditionOptions = Array.isArray(patientConditions)
    ? patientConditions.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean)
    : [];

  const handleAddRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `area-${window.crypto.randomUUID()}` : `area-${Math.random().toString(36).slice(2, 9)}`,
      area: '',
      pincode: '',
      city: '',
      calls_per_month: '',
      inbound_percent: '',
      primary_type: '',
      current_supply: ''
    };
    onChange([...rows, newRow]);
  };

  const handleRemoveRow = (indexToRemove) => {
    if (rows.length <= 1) return;
    const filtered = rows.filter((_, idx) => idx !== indexToRemove);
    onChange(filtered);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = rows.map((row, idx) => {
      if (idx === index) {
        return { ...row, [field]: value };
      }
      return row;
    });
    onChange(updated);
  };

  return (
    <div className="w-full">
      {/* Title & Add Row */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
            <MapPin className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 leading-tight">
              Primary service areas & PIN codes
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Locality-wise demand estimates and patient incoming catchment zones.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddRow}
          aria-label="Add new area demand row"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium cursor-pointer transition-colors shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add area</span>
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50/80 text-slate-600 font-semibold border-b border-slate-200">
              <th className="py-3 px-3 w-10 text-center">#</th>
              <th className="py-3 px-3 min-w-[140px]">Area Name</th>
              <th className="py-3 px-3 min-w-[110px]">PIN Code</th>
              <th className="py-3 px-3 min-w-[110px]">City</th>
              <th className="py-3 px-3 min-w-[100px]">Calls / Month</th>
              <th className="py-3 px-3 min-w-[100px]">Inbound %</th>
              <th className="py-3 px-3 min-w-[150px]">Primary Type</th>
              <th className="py-3 px-3 min-w-[130px]">Current Supply</th>
              <th className="py-3 px-2 w-10 text-center"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((row, idx) => (
              <tr key={row.id || idx} className="hover:bg-slate-50/60 transition-colors">
                <td className="py-2.5 px-3 text-center text-slate-400 font-medium">
                  {idx + 1}
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="text"
                    placeholder="Area name"
                    aria-label={`Area name for row ${idx + 1}`}
                    value={row.area || ''}
                    onChange={(e) => handleFieldChange(idx, 'area', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="text"
                    placeholder="PIN code"
                    maxLength={6}
                    aria-label={`PIN code for row ${idx + 1}`}
                    value={row.pincode || ''}
                    onChange={(e) => handleFieldChange(idx, 'pincode', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="text"
                    placeholder="City"
                    aria-label={`City for row ${idx + 1}`}
                    value={row.city || ''}
                    onChange={(e) => handleFieldChange(idx, 'city', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="number"
                    min="0"
                    placeholder="Calls"
                    aria-label={`Calls per month for row ${idx + 1}`}
                    value={row.calls_per_month || ''}
                    onChange={(e) => handleFieldChange(idx, 'calls_per_month', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Inbound %"
                    aria-label={`Inbound percentage for row ${idx + 1}`}
                    value={row.inbound_percent || ''}
                    onChange={(e) => handleFieldChange(idx, 'inbound_percent', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3 min-w-[140px]">
                  <CompactConditionSelect
                    value={row.primary_type || ''}
                    onChange={(val) => handleFieldChange(idx, 'primary_type', val)}
                    options={conditionOptions}
                    placeholder="Select type"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <select
                    aria-label={`Current supply assessment for row ${idx + 1}`}
                    value={row.current_supply || ''}
                    onChange={(e) => handleFieldChange(idx, 'current_supply', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  >
                    <option value="">Select supply</option>
                    <option value="Sufficient">Sufficient</option>
                    <option value="Moderate deficit">Moderate Deficit</option>
                    <option value="Severe shortage">Severe Shortage</option>
                  </select>
                </td>
                <td className="py-2.5 px-2 text-center">
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(idx)}
                      aria-label={`Remove area row ${idx + 1}`}
                      className="text-slate-400 hover:text-red-600 transition-colors p-1 rounded-md cursor-pointer"
                      title="Remove row"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicAreaDemandTable;
